// useAuthImage — fetches a URL with the Telegram initData Authorization header
// and exposes it as a blob: URL suitable for <img src=...>. Telegram WebView
// won't pass our auth header on a plain <img>, so the file endpoint is
// proxied through fetch.
//
// Append-only attachments UI uses this for thumbnails (see
// FlexAttachmentsScreen / FlexOrderDetail).

import { useEffect, useState } from "react";
import type { ImgHTMLAttributes } from "react";

export function useAuthImage(url: string | null): string | null {
  const [blob, setBlob] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setBlob(null);
      return;
    }
    const tg = (window as any).Telegram?.WebApp;
    let cancelled = false;
    let toRevoke: string | null = null;

    fetch(url, { headers: { authorization: `tma ${tg?.initData || ""}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b || cancelled) return;
        toRevoke = URL.createObjectURL(b);
        setBlob(toRevoke);
      })
      .catch(() => {
        // молча — компонент покажет skeleton
      });

    return () => {
      cancelled = true;
      if (toRevoke) URL.revokeObjectURL(toRevoke);
    };
  }, [url]);

  return blob;
}

type AuthImgProps = { url: string } & ImgHTMLAttributes<HTMLImageElement>;

export function AuthImg({ url, style, ...rest }: AuthImgProps) {
  const blob = useAuthImage(url);
  if (!blob) {
    return (
      <div
        className="img-skeleton"
        style={{
          width: rest.width,
          height: rest.height,
          background: "var(--mnp-gray-200)",
          borderRadius: 8,
          ...style,
        }}
      />
    );
  }
  return <img src={blob} style={style} {...rest} />;
}
